// MobileTabBar — bottom tab bar, shown only at <768px.
//
// Pixel-mapped from the design `.mobile-tabbar` (employee-portal 2/project/
// mobile.css). Carries the primary destinations that the top nav drops on
// mobile. Always in the DOM; visibility is purely a breakpoint concern, matching
// the design.
//
// Layout: it's the last flex child of the app-shell column (see
// pages/Members/index.tsx). On mobile the shell is a fixed-height
// (100dvh) flex column whose <main> scrolls internally, so this bar sits
// permanently at the bottom of the screen — it never scrolls away, and page
// content always scrolls *above* it (nothing is ever hidden behind it).

import { Box, Icon } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import MFMembers from "state/members/membersState";
import Linker from "ui/Linker";
import { MOBILE_TABS, useVisibleNavItems, type NavItem } from "./navConfig";

const TabBadge = ({ count }: { count: number }) => (
  <Box
    as="span"
    position="absolute"
    top="2px"
    left="50%"
    ml="6px"
    minW="16px"
    h="16px"
    px="4px"
    bg="error.500"
    color="white"
    fontSize="2xs" // 10px
    fontWeight={600}
    lineHeight={1}
    borderRadius="component.lg" // 8px
    border="1.5px solid"
    borderColor="neutral.25"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
  >
    {count > 99 ? "99+" : count}
  </Box>
);

const TabItem = ({
  item,
  active,
  count,
}: {
  item: NavItem;
  active: boolean;
  count?: number;
}) => (
  <Linker
    to={item.to}
    aria-current={active ? "page" : undefined}
    flex="1 1 0"
    position="relative"
    display="inline-flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="2px"
    fontFamily="body"
    fontWeight={500}
    borderRadius="component.xl" // 10px
    color={active ? "azurite.600" : "neutral.700"}
    transition="color 120ms, background 120ms"
    _hover={{ textDecoration: "none", bg: "neutral.50" }}
    sx={{ padding: "4px 4px 6px", fontSize: "10.5px" }}
  >
    <Icon as={item.icon} boxSize="22px" />
    <Box as="span">{item.shortLabel}</Box>
    {count !== undefined && count > 0 && <TabBadge count={count} />}
  </Linker>
);

const MobileTabBar = () => {
  const { pathname } = useLocation();
  const stats = MFMembers.useGetStats();
  const navItems = useVisibleNavItems(MOBILE_TABS);

  const taskCount = stats
    ? (stats.provisioning || 0) +
      (stats.tasks || 0) +
      (stats.deprovisioning || 0) +
      (stats.entitlementAdjustment || 0)
    : 0;

  return (
    <Box
      as="nav"
      aria-label="Primary"
      display={{ base: "flex", md: "none" }}
      flexShrink={0}
      h="64px"
      bg="neutral.25"
      borderTop="1px solid"
      borderColor="neutral.200"
      zIndex={80}
      boxShadow="0 -4px 12px rgba(16, 24, 40, 0.04)"
      sx={{
        // height grows by the home-indicator inset so the bar always clears it.
        height: "calc(64px + env(safe-area-inset-bottom))",
        padding: "6px 4px",
        paddingBottom: "calc(6px + env(safe-area-inset-bottom))",
      }}
    >
      {navItems.map((item) => (
        <TabItem
          key={item.id}
          item={item}
          active={item.isActive(pathname)}
          count={item.hasCount ? taskCount : undefined}
        />
      ))}
    </Box>
  );
};

export default MobileTabBar;
